'use client';

import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  forwardRef,
} from 'react';
import { Menu, MoveRight, X } from 'lucide-react';
import { Image } from '@/hen-ui/atomics/Image';
import { NavigationWrapper } from '@/hen-ui/atomics/NavigationWrapper';
import { Link } from '@/hen-ui/atomics/Link';
import { Button } from '@/hen-ui/atomics/Button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
  DrawerHeader,
  DrawerTitle,
} from '@/hen-ui/composites/Drawer';
import { PopoverInlineProvider } from '@/hen-ui/composites/Popover/popover-inline-context';
import { PopoverInsideNavbarBarProvider } from '@/hen-ui/composites/Popover/popover-navbar-context';
import {
  MobileLayout,
  hasNavSubmenu,
  hasValidHref,
  normalizeNavHref,
  normalizeNavItems,
} from '@/hen-ui/composites/SubNav';
import { SubNavPanel } from '@/hen-ui/composites/SubNav/SubNav';
import type { NavItem, SubNavCta } from '@/hen-ui/composites/SubNav';
import { useReducedMotion } from '@/hen-ui/hooks/useReducedMotion';
import { outsideCloseVariants } from '@/hen-ui/lib/outside-close.variants';
import { usePortalContainer } from '@/hen-ui/providers/PortalContainerContext';
import { cn } from '@/hen-ui/utils';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { useCatalogEditing } from '@/hen-ui/catalog/editing-context';
import {
  NAV_HIGHLIGHT_ACTIVE_CLASS,
  NAV_HIGHLIGHT_CLASS,
  NAV_HIGHLIGHT_GAP_CLASS,
  NAV_LINK_TRANSITION_CLASS,
  NAVBAR_BAR_PX,
  NAVBAR_BAR_PX_DESKTOP,
  NAVBAR_COLLAPSE_CLASSES,
  NAVBAR_LINK_WEIGHT_CLASS,
  navbarBarRowVariants,
  navbarLogoVariants,
  navbarPanelContentVariants,
  navbarPanelSurfaceVariants,
  navbarPanelVariants,
  navbarVariants,
  type NavbarBarPadding,
  type NavbarBottomEdge,
  type NavbarCollapseBelow,
  type NavbarContentWidth,
  type NavbarDrawerClosePlacement,
  type NavbarDrawerBackColor,
  type NavbarDrawerHeader,
  type NavbarDrawerHeaderDivider,
  type NavbarDrawerItemSize,
  type NavbarDrawerSize,
  type NavbarLinkWeight,
  type NavbarDrawerTransition,
  type NavbarHoverStyle,
  type NavbarLogoSize,
  type NavbarMenuColumns,
  type NavbarMenuLabelStyle,
  type NavbarMenuLinkHover,
  type NavbarMenuLinkStyle,
  type NavbarMenuTransition,
  type NavbarPanelOverlay,
  type NavbarPanelShadow,
  type NavbarPanelShape,
  type NavbarPanelWidth,
  type NavbarSpace,
  type NavbarVariantProps,
} from './navbar.variants';
import { SPACE_GAP } from '@/hen-ui/utils/space-steps';

const CLOSE_DELAY_MS = 150;
// `animationend` does not fire when the animation is skipped, for example in
// a background tab, so the closing fade has a deadline. It never cuts a
// themed duration short: a longer fade moves the deadline past its end.
const MENU_EXIT_DEADLINE_MS = 600;
const MENU_EXIT_DEADLINE_BUFFER_MS = 100;
// The theme's animation scale has no `none`, so `animate-none` is not generated.
const MENU_FADE_DURATION_CLASS =
  '[--motion-duration:var(--motion-duration-normal,200ms)] motion-reduce:[animation:none]';
const MENU_FADE_IN_CLASS = `animate-fade-in-motion ${MENU_FADE_DURATION_CLASS}`;
const MENU_FADE_OUT_CLASS = `pointer-events-none animate-fade-out-motion [animation-fill-mode:forwards] ${MENU_FADE_DURATION_CLASS}`;

function cssTimeToMs(value: string): number {
  const trimmed = value.trim();
  const amount = Number.parseFloat(trimmed);
  if (!Number.isFinite(amount)) return 0;
  return trimmed.endsWith('ms') ? amount : amount * 1000;
}

function menuExitDeadlineMs(element: HTMLElement | null): number {
  if (!element) return MENU_EXIT_DEADLINE_MS;
  const longest = Math.max(
    0,
    ...getComputedStyle(element).animationDuration.split(',').map(cssTimeToMs),
  );
  return Math.max(
    MENU_EXIT_DEADLINE_MS,
    longest + MENU_EXIT_DEADLINE_BUFFER_MS,
  );
}

// The drawer backdrop takes the theme's overlay color when one is set and
// keeps the Drawer's 40% black otherwise. The fixed opacity keeps the
// backdrop from fading, as before.
const DRAWER_OVERLAY_CLASS = 'bg-nav-drawer-overlay !opacity-100';

const DRAWER_PUSH_DURATION = 'var(--motion-duration-slow, 300ms)';
const DRAWER_PUSH_EASING =
  'var(--motion-easing-default, cubic-bezier(0.4, 0, 0.2, 1))';
const DRAWER_PUSH_FALLBACK_MS = 300;
const DRAWER_PUSH_BUFFER_MS = 100;
const DRAWER_PUSH_ANIMATION_STYLE: React.CSSProperties = {
  animationDuration: DRAWER_PUSH_DURATION,
  animationTimingFunction: DRAWER_PUSH_EASING,
};

// Read from the drawer, which sits inside the theme scope, rather than the
// document root.
function drawerPushMs(drawer: HTMLElement): number {
  const value = getComputedStyle(drawer).getPropertyValue(
    '--motion-duration-slow',
  );
  return (
    (value.trim() ? cssTimeToMs(value) : DRAWER_PUSH_FALLBACK_MS) +
    DRAWER_PUSH_BUFFER_MS
  );
}

const DRAWER_PUSH_TARGET = '[data-drawer-push-target]';

// Only elements the host marks are pushed. `drawerRoot` is the drawer, or the
// container it portals into before it mounts. A target that holds it would
// carry the drawer along, so it is skipped, and a target inside another
// target moves with the outer one.
function pushTargets(drawerRoot: HTMLElement | undefined): HTMLElement[] {
  const targets = Array.from(
    document.querySelectorAll<HTMLElement>(DRAWER_PUSH_TARGET),
  ).filter(
    (target) =>
      !drawerRoot ||
      (!target.contains(drawerRoot) && !drawerRoot.contains(target)),
  );
  return targets.filter(
    (target) =>
      !targets.some((other) => other !== target && other.contains(target)),
  );
}

interface PushedElement {
  element: HTMLElement;
  transform: string;
  transition: string;
}

/**
 * Slides the push targets by the drawer's width while `active`, and back when
 * it ends. Inline styles the targets already had are restored once the slide
 * back is done.
 */
function useDrawerPagePush(
  active: boolean,
  direction: NavbarDrawerDirection,
  drawerRef: React.RefObject<HTMLElement | null>,
): void {
  const finishRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!active) return undefined;
    finishRef.current?.();
    let pushed: PushedElement[] = [];
    let resizeObserver: ResizeObserver | undefined;
    // `drawerRef.current` is already null when the cleanup runs.
    let pushedBy: HTMLElement | null = null;
    const frame = requestAnimationFrame(() => {
      const drawer = drawerRef.current;
      if (!drawer) return;
      pushedBy = drawer;
      const offset = () =>
        `translateX(${direction === 'left' ? drawer.offsetWidth : -drawer.offsetWidth}px)`;
      pushed = pushTargets(drawer).map((element) => {
        const saved = {
          element,
          transform: element.style.transform,
          transition: element.style.transition,
        };
        element.style.transition = `transform ${DRAWER_PUSH_DURATION} ${DRAWER_PUSH_EASING}`;
        element.style.transform = offset();
        return saved;
      });
      // The drawer's width follows the viewport and `drawerSize`, so the
      // page keeps the offset while the drawer stays open.
      if (typeof ResizeObserver === 'undefined') return;
      resizeObserver = new ResizeObserver(() => {
        const transform = offset();
        for (const { element } of pushed) element.style.transform = transform;
      });
      resizeObserver.observe(drawer);
    });

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      if (pushed.length === 0 || !pushedBy) return;
      for (const { element, transform } of pushed) {
        element.style.transform = transform;
      }
      const finish = () => {
        window.clearTimeout(timer);
        for (const { element, transition } of pushed) {
          element.style.transition = transition;
        }
        finishRef.current = null;
      };
      const timer = window.setTimeout(finish, drawerPushMs(pushedBy));
      finishRef.current = finish;
    };
  }, [active, direction, drawerRef]);

  useEffect(() => () => finishRef.current?.(), []);
}

/**
 * Catalog documents may still store flat links as strings. A string becomes
 * a label with no destination (`href: '#'`), and the bar renders that label
 * as text rather than a link. A blank label is dropped even when the href
 * is real, so the bar never shows a link with no accessible name.
 */
function normalizeNavLinks(links: unknown): NavbarLinkItem[] {
  if (!Array.isArray(links)) return [];

  return links.flatMap((link): NavbarLinkItem[] => {
    if (typeof link === 'string') {
      const label = link.trim();
      return label ? [{ label, href: '#' }] : [];
    }
    if (!link || typeof link !== 'object') return [];

    const record = link as NavbarLinkItem;
    const label = typeof record.label === 'string' ? record.label.trim() : '';
    if (!label) return [];

    return [
      {
        label,
        href: normalizeNavHref(record.href),
        ...(record.active === true ? { active: true } : {}),
        ...(typeof record.onClick === 'function'
          ? { onClick: record.onClick }
          : {}),
      },
    ];
  });
}

export type NavbarMenuTriggerPlacement = 'leading' | 'trailing';
export type NavbarDrawerDirection = 'left' | 'right' | 'top';

export interface NavbarLinkItem {
  /** Link label */
  label: string;
  /**
   * Link destination. `'#'` renders as text. Set `onClick` to keep it a link
   * so a code consumer can handle the click.
   */
  href: string;
  /** Whether the link is active */
  active?: boolean;
  /** Click handler. Keeps an `href` of `'#'` as a focusable link. */
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

/** Catalog `'#'` labels are text. A click handler keeps the usual link. */
function isTextOnlyNavLink(link: NavbarLinkItem): boolean {
  return link.href === '#' && link.onClick === undefined;
}

interface NavbarBarFrameProps extends React.HTMLAttributes<HTMLDivElement> {
  capped: boolean;
  rowClassName: string;
  paddingClassName: string;
}

/**
 * The bar row. Under a cap the side padding moves to a full-width wrapper,
 * so the capped row is the content width; uncapped, the row keeps it.
 */
function NavbarBarFrame({
  capped,
  rowClassName,
  paddingClassName,
  children,
  ...rowProps
}: NavbarBarFrameProps) {
  if (!capped) {
    return (
      <div className={cn(rowClassName, paddingClassName)} {...rowProps}>
        {children}
      </div>
    );
  }
  return (
    <div className={paddingClassName}>
      <div className={rowClassName} {...rowProps}>
        {children}
      </div>
    </div>
  );
}

export interface NavbarProps
  extends React.HTMLAttributes<HTMLElement>,
    NavbarVariantProps {
  /** Source URL of the logo image */
  logoSrc?: string;
  /** Navigation destination for the logo */
  logoHref?: string;
  /**
   * Hierarchical navigation items. Items with `children` get a SubNav flyout
   * on desktop and a drill-down drawer on mobile. Takes precedence over `links`.
   * A top-level item's `layout` picks a sidebar of sections (default) or a
   * flat row of columns. Missing ids are derived from the item's position.
   */
  items?: NavItem[];
  /**
   * Simple flat links (no SubNav). Used when `items` is not provided.
   */
  links?: NavbarLinkItem[];
  /** Action elements (buttons, user menu, etc.) */
  actions?: React.ReactNode;
  /**
   * `NavbarMenuContent` blocks for drop-down menus. Each block shows in the
   * side column of the menu whose label matches its `menu`, replacing that
   * menu's featured or divided column, and on the menu's mobile tier.
   */
  menuContent?: React.ReactNode;
  /** CTA block shown at the bottom of each SubNav panel */
  subNavCta?: SubNavCta;
  /** Separates the last column in each SubNav panel with a visual divider */
  dividedLastColumn?: boolean;
  /**
   * When false, desktop drop-down panels omit the heading above their
   * columns (the menu or section label). The mobile drawer is unaffected.
   * @default true
   */
  showMenuHeading?: boolean;
  /**
   * Title of the small-screen drawer. Screen readers announce it, and
   * `drawerHeader="title"` shows it in the header.
   * @default 'Navigation Menu'
   */
  drawerTitle?: string;
  /** Conditionally hides/shows the mobile menu */
  showDrawerMenu?: boolean;
  /** Add additional classes to the image component */
  imageClassName?: string;
  /**
   * Logo height. Each size reads its own theme variable
   * (`--cds-navbar-logo-height-sm` / `--cds-navbar-logo-height` /
   * `--cds-navbar-logo-height-lg`). `imageClassName` is applied on top.
   * @default 'md'
   */
  logoSize?: NavbarLogoSize;
  /**
   * Side padding of the bar content, a step of the space scale, or `'page'`
   * for the page side padding (`--space-page-sides`) the page layouts use. A
   * named `contentWidth` or `panelWidth` caps the content and the padding
   * sits outside the cap, as in the page layouts.
   * @default 'xl'
   */
  barPaddingX?: NavbarBarPadding;
  /**
   * Space between the logo and the top-level links, a step of the space
   * scale.
   * @default '2xl'
   */
  logoGap?: NavbarSpace;
  /**
   * Caps the bar's logo, links, and actions at a layout width and centers
   * them: `'content'` is the page content width (`--width-content`),
   * `'article'` the article column width (`--width-article-content`). The
   * bar background and bottom edge still span the viewport.
   * @default 'full'
   */
  contentWidth?: NavbarContentWidth;
  /**
   * Line under the bar. `'shadow'` uses the small shadow token
   * (`--shadow-sm`). Unset follows `variant`: a border on `'solid'`, none
   * otherwise.
   */
  bottomEdge?: NavbarBottomEdge;
  /**
   * Width of the desktop drop-down panel, from the same widths as
   * `contentWidth`. A `'card'` panel is capped and centered; a
   * `'full-width'` panel keeps its edge-to-edge surface and caps its
   * content. Unset follows `contentWidth`.
   */
  panelWidth?: NavbarPanelWidth;
  /**
   * Shadow under the desktop drop-down panel. Unset follows `panelShape`:
   * none on `'card'`, `'lg'` on `'full-width'`.
   */
  panelShadow?: NavbarPanelShadow;
  /**
   * `'dim'` darkens the page behind an open drop-down panel and holds page
   * scrolling. `'none'` leaves the page as is; selecting outside the panel
   * still closes it.
   * @default 'dim'
   */
  panelOverlay?: NavbarPanelOverlay;
  /**
   * `'fade'` fades the drop-down panel and its overlay in and out, and
   * cross-fades sections. Timing follows `--motion-duration-normal`. Reduced
   * motion turns it off.
   * @default 'none'
   */
  menuTransition?: NavbarMenuTransition;
  /**
   * Width of the link columns in a drop-down panel. `'fill'` shares the row
   * between them. `'fixed'` gives each column `--cds-subnav-column-width`,
   * and inline menu content follows the last column one column gap later.
   * @default 'fill'
   */
  menuColumns?: NavbarMenuColumns;
  /**
   * Side padding of the drop-down panel content, a step of the space scale.
   * Unset keeps the responsive 24/32/40px padding.
   */
  menuPaddingX?: NavbarSpace;
  /**
   * Top and bottom padding of the drop-down panel content, a step of the
   * space scale. Unset keeps the responsive 24/32/40px padding.
   */
  menuPaddingY?: NavbarSpace;
  /** Bottom padding of the drop-down panel content. Unset follows `menuPaddingY`. */
  menuPaddingBottom?: NavbarSpace;
  /**
   * Space between link columns, and before inline menu content, a step of
   * the space scale.
   * @default '2xl'
   */
  menuColumnGap?: NavbarSpace;
  /**
   * Space between the links in a drop-down column on larger screens, a step
   * of the space scale. Set, it also spaces the drawer links with
   * `drawerItemSize="compact"`; unset keeps the drawer's `xxs`.
   * @default 'xs'
   */
  menuLinkGap?: NavbarSpace;
  /**
   * Space above each drop-down column heading, a step of the space scale.
   * @default 'xxs'
   */
  menuLabelTop?: NavbarSpace;
  /**
   * Drop-down and drawer column headings. `'heading'` is bold in the heading
   * color; `'subtext'` is regular weight in the subtext color.
   * @default 'heading'
   */
  menuLabelStyle?: NavbarMenuLabelStyle;
  /**
   * Drop-down column links. `'subtext'` is b4 (b5 between `lg` and `xl`) in
   * the subtext color; `'body'` is b5 at every width in the body color. In
   * the drawer, `'body'` links are b3 in the body color.
   * @default 'subtext'
   */
  menuLinkStyle?: NavbarMenuLinkStyle;
  /**
   * What a drop-down panel link does on hover on larger screens. `'underline'`
   * turns the text the secondary color and underlines it; `'none'` leaves the
   * link as it is; `'primary'` turns the text the primary color without an
   * underline. The small-screen menu rows and any menu content are not
   * affected.
   * @default 'underline'
   */
  menuLinkHover?: NavbarMenuLinkHover;
  /**
   * Horizontal placement of the logo. `'center'` absolutely centers the logo
   * in the bar while links stay left-aligned and actions stay right-aligned.
   * Defaults to `'left'`.
   *
   * Note: `'center'` overlays the logo across the full bar width, so it is
   * intended for minimal bars (e.g. logo-only or logo + menu, as in the
   * `CenteredLogo` story). Combining a centered logo with both left `links`
   * and right `actions` on a narrow viewport can cause the logo to overlap
   * them.
   */
  logoPosition?: 'left' | 'center';
  /**
   * Hover treatment for desktop top-level links. `'underline'` underlines the
   * label; `'highlight'` adds a tinted pill with an accent underline, kept on
   * while that item's drop-down menu is open. Highlight links sit flush, so
   * adjacent pills touch and labels are 48px apart. The accent underline grows
   * from its center over `--cds-navbar-transition-duration` (instant when
   * unset or under reduced motion). Colors come from
   * `--cds-color-nav-link-hover-bg` / `--cds-color-nav-link-hover-accent`,
   * falling back to the primary palette.
   * @default 'underline'
   */
  hoverStyle?: NavbarHoverStyle;
  /**
   * Shape of the desktop drop-down panel. `'card'` insets a rounded panel
   * from the viewport edges; `'full-width'` runs a square panel edge to edge,
   * flush under the bar.
   * @default 'card'
   */
  panelShape?: NavbarPanelShape;
  /**
   * Where the menu button sits on small screens. `'leading'` places it before
   * the logo, `logoGap` apart. `'trailing'` keeps it at the end of the bar.
   * Hidden from the `collapseBelow` width either way.
   * @default 'trailing'
   */
  menuTriggerPlacement?: NavbarMenuTriggerPlacement;
  /**
   * When true, `actions` stay visible on the bar below the `collapseBelow`
   * width, in the trailing group and before a trailing menu button. They are
   * not repeated inside the flat-link drawer.
   * @default false
   */
  showMobileActions?: boolean;
  /**
   * Edge the drawer enters from below the `collapseBelow` width. `'left'`
   * and `'right'` use a `drawerSize` panel. `'top'` uses the full height.
   * Unset keeps the 640px split: full height from the top below 640px, from
   * the right at 640px and up.
   */
  drawerDirection?: NavbarDrawerDirection;
  /**
   * Screen width where the bar switches from the menu button and drawer to
   * the desktop links and drop-down panels: `'md'` is 768px, `'lg'` 1024px.
   * @default 'lg'
   */
  collapseBelow?: NavbarCollapseBelow;
  /**
   * Width of a left or right drawer, as a share of the screen. The top
   * drawer is always full width.
   * @default '1/2'
   */
  drawerSize?: NavbarDrawerSize;
  /**
   * Drawer header. `'compact'` is a row of controls with a back link named
   * for the previous level, and each level's title below it. `'title'` shows
   * `drawerTitle` on the first level and a back arrow with the level's title
   * on the others. The header stays pinned while the drawer scrolls.
   * @default 'compact'
   */
  drawerHeader?: NavbarDrawerHeader;
  /**
   * Shows the drawer button that filters the menu sections.
   * @default true
   */
  drawerSearch?: boolean;
  /**
   * Drawer row size. `'compact'` is small text in the heading color.
   * `'touch'` is a touch-size row in body text, and each column link is a
   * full row.
   * @default 'compact'
   */
  drawerItemSize?: NavbarDrawerItemSize;
  /**
   * Lines between the drawer rows and column groups, and above the featured
   * and menu content blocks.
   * @default true
   */
  drawerDividers?: boolean;
  /**
   * Line under the drawer header and filter row. `'subtle'` is neutral-100,
   * `'divider'` and `'strong'` follow `--border-divider` and
   * `--border-divider-strong`, and `'accent'` is the nav hover accent.
   * @default 'subtle'
   */
  drawerHeaderDivider?: NavbarDrawerHeaderDivider;
  /**
   * Color of the drawer back button. `'accent'` is primary-700 and
   * `'neutral'` is the heading color. Unset follows `drawerHeader`:
   * `'accent'` with the compact header, `'neutral'` with the title header.
   */
  drawerBackColor?: NavbarDrawerBackColor;
  /**
   * Weight of the desktop top-level links and of the drawer rows.
   * @default 'bold'
   */
  linkWeight?: NavbarLinkWeight;
  /**
   * `'slide'` moves the drawer over the page. `'push'` also slides the
   * elements the host marks with `data-drawer-push-target` aside with a left
   * or right drawer; both follow `--motion-duration-slow` and
   * `--motion-easing-default`. Without a marked element outside the drawer,
   * `'push'` behaves like `'slide'`. While pushed, a target has a transform,
   * so it becomes the containing block for its `position: fixed`
   * descendants. Reduced motion keeps the page still.
   * @default 'slide'
   */
  drawerTransition?: NavbarDrawerTransition;
  /**
   * Where the drawer's close button sits. `'header'` keeps it in the drawer
   * header. `'outside'` puts a white close icon on the backdrop just past the
   * open edge of a left or right drawer, and the header keeps its title
   * without it. A top or full-width drawer has no backdrop beside it, so it
   * keeps the header button. The icon has no fill and reads against the
   * dimmed backdrop.
   * @default 'header'
   */
  drawerClosePlacement?: NavbarDrawerClosePlacement;
}

/**
 * Navbar component — responsive top navigation with automatic mobile drawer.
 *
 * When `items` (hierarchical `NavItem[]`) is provided, top-level items with
 * children render a SubNav flyout on desktop and a drill-down experience on
 * mobile, including section-level search. Simple flat `links` are supported
 * for cases that don't need sub-navigation.
 *
 * @example
 * // Simple flat links
 * <Navbar
 *   logoSrc="/logo.svg"
 *   logoHref="/"
 *   links={[{ label: 'Home', href: '/' }, { label: 'About', href: '/about' }]}
 *   actions={<Button>Sign In</Button>}
 * />
 *
 * @example
 * // Hierarchical items with SubNav
 * <Navbar
 *   logoSrc="/logo.svg"
 *   logoHref="/"
 *   items={hierarchicalMenu}
 *   subNavCta={{ text: 'Find a program', buttonText: 'Search', href: '/search' }}
 * />
 */
export const Navbar = forwardRef<HTMLElement, NavbarProps>(
  (
    {
      className,
      variant,
      sticky,
      logoSrc,
      logoHref,
      items: rawItems,
      links: rawLinks = [],
      actions,
      menuContent,
      subNavCta,
      dividedLastColumn = false,
      showMenuHeading = true,
      drawerTitle = 'Navigation Menu',
      showDrawerMenu = true,
      imageClassName = '',
      logoSize,
      contentWidth = 'full',
      bottomEdge,
      panelWidth,
      panelShadow,
      panelOverlay = 'dim',
      menuTransition = 'none',
      menuColumns = 'fill',
      barPaddingX,
      logoGap,
      menuPaddingX,
      menuPaddingY,
      menuPaddingBottom,
      menuColumnGap,
      menuLinkGap,
      menuLabelTop,
      menuLabelStyle,
      menuLinkStyle,
      menuLinkHover,
      logoPosition = 'left',
      hoverStyle = 'underline',
      panelShape = 'card',
      menuTriggerPlacement = 'trailing',
      showMobileActions = false,
      drawerDirection,
      collapseBelow,
      drawerSize,
      drawerHeader,
      drawerSearch = true,
      drawerItemSize,
      drawerDividers = true,
      drawerHeaderDivider,
      drawerBackColor,
      linkWeight,
      drawerTransition,
      drawerClosePlacement,
      ...props
    },
    ref,
  ) => {
    const items = useMemo(() => normalizeNavItems(rawItems), [rawItems]);
    const links = useMemo(() => normalizeNavLinks(rawLinks), [rawLinks]);
    const isHighlight = hoverStyle === 'highlight';
    const resolvedLinkWeight = linkWeight || 'bold';
    const linkWeightClass = NAVBAR_LINK_WEIGHT_CLASS[resolvedLinkWeight];
    const useHierarchical = items.length > 0;
    const isEditing = useCatalogEditing();
    const reducedMotion = useReducedMotion();
    const fadesMenu = menuTransition === 'fade';
    const dimsPage = panelOverlay === 'dim';
    const collapse = collapseBelow || 'lg';
    const collapseClasses = NAVBAR_COLLAPSE_CLASSES[collapse];

    const [activeSubNav, setActiveSubNav] = useState<number | null>(null);
    // A closing panel stays mounted to fade out. Tracking the previous open
    // item during render keeps the panel on screen through the swap.
    const [closingSubNav, setClosingSubNav] = useState<number | null>(null);
    const [trackedSubNav, setTrackedSubNav] = useState<number | null>(null);
    if (trackedSubNav !== activeSubNav) {
      setTrackedSubNav(activeSubNav);
      setClosingSubNav(
        activeSubNav === null && fadesMenu && !reducedMotion
          ? trackedSubNav
          : null,
      );
    }
    const containerRef = useRef<HTMLDivElement>(null);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [hasPushTarget, setHasPushTarget] = useState(false);
    const portalContainer = usePortalContainer();
    const [isTabletDrawer, setIsTabletDrawer] = useState(false);
    const drawerContentRef = useRef<HTMLDivElement>(null);
    const subNavRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLElement | null>(null);
    const openedViaKeyboard = useRef(false);

    const cancelScheduledClose = useCallback(() => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    }, []);

    const scheduleClose = useCallback(() => {
      cancelScheduledClose();
      closeTimeoutRef.current = setTimeout(() => {
        setActiveSubNav(null);
      }, CLOSE_DELAY_MS);
    }, [cancelScheduledClose]);

    const closeSubNav = useCallback(() => {
      cancelScheduledClose();
      setActiveSubNav(null);
    }, [cancelScheduledClose]);

    useEffect(() => {
      function handleClickOutside(event: globalThis.MouseEvent) {
        if (
          !isEditing &&
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          closeSubNav();
        }
      }

      function handleEscape(event: KeyboardEvent) {
        if (event.key === 'Escape' && activeSubNav !== null) {
          closeSubNav();
          triggerRef.current?.focus();
          triggerRef.current = null;
        }
      }

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }, [closeSubNav, activeSubNav, isEditing]);

    useEffect(() => {
      return () => cancelScheduledClose();
    }, [cancelScheduledClose]);

    const finishClosing = useCallback(() => setClosingSubNav(null), []);

    useEffect(() => {
      if (closingSubNav === null) return undefined;
      const timer = window.setTimeout(
        finishClosing,
        menuExitDeadlineMs(subNavRef.current),
      );
      return () => window.clearTimeout(timer);
    }, [closingSubNav, finishClosing]);

    useEffect(() => {
      if (activeSubNav !== null && openedViaKeyboard.current) {
        openedViaKeyboard.current = false;
        requestAnimationFrame(() => {
          const firstFocusable = subNavRef.current?.querySelector<HTMLElement>(
            'a[href], button:not([disabled])',
          );
          firstFocusable?.focus();
        });
      }
    }, [activeSubNav]);

    // Prevent scrolling while SubNav is open by blocking wheel, touch, and
    // keyboard scroll events. Unlike overflow:hidden this keeps the scrollbar
    // visible so there is no layout shift or gap. Without the dim overlay the
    // page stays scrollable.
    useEffect(() => {
      const open =
        useHierarchical &&
        activeSubNav !== null &&
        hasNavSubmenu(items[activeSubNav ?? -1]);
      if (!open || isEditing || !dimsPage) return;

      const prevent = (e: Event) => e.preventDefault();
      const preventKeys = (e: KeyboardEvent) => {
        const scrollKeys = new Set([
          'ArrowUp',
          'ArrowDown',
          'Space',
          'PageUp',
          'PageDown',
          'Home',
          'End',
        ]);
        if (scrollKeys.has(e.code)) e.preventDefault();
      };

      document.addEventListener('wheel', prevent, { passive: false });
      document.addEventListener('touchmove', prevent, { passive: false });
      document.addEventListener('keydown', preventKeys, { passive: false });
      return () => {
        document.removeEventListener('wheel', prevent);
        document.removeEventListener('touchmove', prevent);
        document.removeEventListener('keydown', preventKeys);
      };
    }, [useHierarchical, activeSubNav, items, isEditing, dimsPage]);

    // Track whether we're in the tablet range (sm–lg: 640px–1023px) so the
    // drawer slides in from the right at 1/3 width instead of top full-screen.
    useEffect(() => {
      const mq = window.matchMedia('(min-width: 640px)');
      setIsTabletDrawer(mq.matches);
      const handler = (e: MediaQueryListEvent) => setIsTabletDrawer(e.matches);
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }, []);

    const panelIndex = activeSubNav ?? closingSubNav;
    const panelItem =
      useHierarchical && panelIndex !== null ? items[panelIndex] : null;
    const isPanelClosing = activeSubNav === null && closingSubNav !== null;
    // The non-sticky variant has no z-index, so positioned page content later
    // in the DOM, and a Popover page dim, would paint over the bar and its
    // open panel and take the pointer. The authoring canvas leaves it
    // unraised: the editor draws its block outlines at a low z-index over
    // the page, and a raised bar would hide them.
    const raisesAbovePage = !isEditing;
    // An empty string is how an authoring host stores "Default", so it
    // follows the other setting just like an unset prop.
    const resolvedPanelWidth = panelWidth || contentWidth || 'full';
    const resolvedPanelShadow =
      panelShadow || (panelShape === 'full-width' ? 'lg' : 'none');
    const barPadding = barPaddingX || 'xl';
    const isBarCapped = !!contentWidth && contentWidth !== 'full';
    const isPanelCapped = resolvedPanelWidth !== 'full';
    const panelStyle = {
      paddingX: menuPaddingX || undefined,
      paddingY: menuPaddingY || undefined,
      paddingBottom: menuPaddingBottom || undefined,
      columnGap: menuColumnGap || undefined,
      linkGap: menuLinkGap || undefined,
      labelTop: menuLabelTop || undefined,
      labelStyle: menuLabelStyle || undefined,
      linkStyle: menuLinkStyle || undefined,
      linkHover: menuLinkHover || undefined,
    };
    const drawerTextStyle = useMemo(
      () => ({
        linkGap: menuLinkGap || undefined,
        labelStyle: menuLabelStyle || undefined,
        linkStyle: menuLinkStyle || undefined,
      }),
      [menuLinkGap, menuLabelStyle, menuLinkStyle],
    );
    const menuFadeClass = fadesMenu
      ? isPanelClosing
        ? MENU_FADE_OUT_CLASS
        : MENU_FADE_IN_CLASS
      : undefined;

    function handleNavMouseOver(event: React.MouseEvent) {
      if (!useHierarchical || isEditing) return;
      const trigger = (event.target as HTMLElement).closest<HTMLElement>(
        '[data-nav-index]',
      );
      if (!trigger) return;

      const index = Number(trigger.dataset.navIndex);
      if (Number.isNaN(index)) return;

      const item = items[index];
      if (!item) return;

      if (hasNavSubmenu(item)) {
        cancelScheduledClose();
        setActiveSubNav(index);
      } else {
        closeSubNav();
      }
    }

    const isLogoCentered = logoPosition === 'center';
    const resolvedDrawerDirection =
      drawerDirection ?? (isTabletDrawer ? 'right' : 'top');
    const isSideDrawer =
      resolvedDrawerDirection === 'left' || resolvedDrawerDirection === 'right';
    const pushesPage =
      drawerTransition === 'push' &&
      hasPushTarget &&
      isSideDrawer &&
      !reducedMotion &&
      !isEditing;
    const resolvedDrawerSize = isSideDrawer ? drawerSize || '1/2' : 'full';
    const closesOutside =
      drawerClosePlacement === 'outside' && resolvedDrawerSize !== 'full';

    useDrawerPagePush(
      pushesPage && isDrawerOpen,
      resolvedDrawerDirection,
      drawerContentRef,
    );

    const LogoImage = logoSrc ? (
      <Image
        src={logoSrc}
        alt='Logo'
        objectFit='contain'
        className={cn(navbarLogoVariants({ logoSize }), imageClassName)}
      />
    ) : null;

    const Logo =
      LogoImage &&
      (logoHref ? (
        <NavigationWrapper
          href={logoHref}
          aria-label='Go to homepage'
          className='min-w-0 rounded-xs focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-offset-focus focus-visible:ring-offset-1'
        >
          {LogoImage}
        </NavigationWrapper>
      ) : (
        LogoImage
      ));

    const menuDrawer = showDrawerMenu ? (
      <Drawer
        open={isDrawerOpen}
        direction={resolvedDrawerDirection}
        dismissible={true}
        onOpenChange={(open: boolean) => {
          if (open && drawerTransition === 'push') {
            setHasPushTarget(pushTargets(portalContainer).length > 0);
          }
          setIsDrawerOpen(open);
        }}
      >
        <DrawerTrigger
          asChild
          className={cn('flex', collapseClasses.mobileOnly)}
        >
          <Button
            variant='textOnly'
            className='p-0 text-nav-link hover:text-nav-link active:text-nav-link [&_svg]:size-6'
            aria-label='Open navigation menu'
          >
            <Menu size={24} />
          </Button>
        </DrawerTrigger>
        <DrawerContent
          ref={drawerContentRef}
          size={resolvedDrawerSize}
          className={cn(closesOutside && 'overflow-visible')}
          overlayClassName={DRAWER_OVERLAY_CLASS}
          style={{
            touchAction: isSideDrawer ? 'pan-x' : 'pan-y',
            ...(pushesPage ? DRAWER_PUSH_ANIMATION_STYLE : undefined),
          }}
          aria-describedby={undefined}
        >
          {/* Visually hidden title for screen-reader accessibility */}
          <DrawerHeader className='sr-only'>
            <DrawerTitle>{drawerTitle}</DrawerTitle>
          </DrawerHeader>

          {closesOutside && (
            <DrawerClose
              aria-label='Close navigation'
              data-vaul-no-drag
              className={outsideCloseVariants({
                side: resolvedDrawerDirection === 'right' ? 'right' : 'left',
                header:
                  useHierarchical && drawerHeader === 'title'
                    ? 'title'
                    : 'compact',
              })}
            >
              <X size={24} aria-hidden='true' />
            </DrawerClose>
          )}

          <div
            className='flex min-h-0 flex-1 flex-col overflow-y-auto'
            data-vaul-no-drag
          >
            {useHierarchical ? (
              <MobileLayout
                items={items}
                cta={subNavCta}
                onClose={() => setIsDrawerOpen(false)}
                menuContent={menuContent}
                header={drawerHeader || 'compact'}
                title={drawerTitle}
                showSearch={drawerSearch}
                showClose={!closesOutside}
                itemSize={drawerItemSize || 'compact'}
                dividers={drawerDividers}
                linkWeight={resolvedLinkWeight}
                headerDivider={drawerHeaderDivider || 'subtle'}
                backColor={drawerBackColor}
                textStyle={drawerTextStyle}
              />
            ) : (
              <>
                {!closesOutside && (
                  <div className='sticky top-0 z-10 flex items-center justify-end bg-white px-sm py-xs'>
                    <DrawerClose asChild>
                      <Button
                        type='button'
                        variant='iconOnly'
                        size='flush'
                        aria-label='Close navigation'
                        className='rounded-xs p-xxs text-neutral-700 focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-offset-focus focus-visible:ring-offset-1 [&_svg]:!size-5'
                      >
                        <X
                          size={20}
                          className='flex-shrink-0'
                          aria-hidden='true'
                        />
                      </Button>
                    </DrawerClose>
                  </div>
                )}
                {/* Simple flat links mobile */}
                <div className='flex flex-col gap-lg p-lg'>
                  {links.length > 0 && (
                    <div className='flex flex-col gap-sm'>
                      {links.map((link, index) => {
                        // `hover:decoration-current` drops Link's
                        // `hover:decoration-link-underline-hover` via twMerge
                        // so drawer links keep a text-colored underline.
                        const className = cn(
                          'flex w-full items-center justify-between rounded-xs py-xxs text-b2 focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-offset-focus focus-visible:ring-offset-1 hover:decoration-current',
                          linkWeightClass,
                        );
                        if (isTextOnlyNavLink(link)) {
                          return (
                            <span
                              key={`mobile-${link.href}-${index}`}
                              className={className}
                            >
                              {link.label}
                            </span>
                          );
                        }
                        return (
                          <Link
                            key={`mobile-${link.href}-${index}`}
                            href={link.href}
                            variant='black'
                            underlineStyle='thin'
                            aria-current={link.active ? 'page' : undefined}
                            className={className}
                            onClick={(e) => {
                              setIsDrawerOpen(false);
                              link.onClick?.(e);
                            }}
                          >
                            {link.label}
                            <MoveRight
                              aria-hidden='true'
                              className='ml-xxs inline-block'
                              size={24}
                            />
                          </Link>
                        );
                      })}
                    </div>
                  )}
                  {actions && !showMobileActions && !isEditing && (
                    // The drawer is already an overlay; a Popover action lays
                    // out its sections here instead of opening on top.
                    <PopoverInlineProvider value={true}>
                      <div className='flex flex-col gap-xxs'>{actions}</div>
                    </PopoverInlineProvider>
                  )}
                </div>
              </>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    ) : null;

    return (
      <nav
        ref={ref}
        className={cn(
          navbarVariants({
            variant,
            sticky,
            bottomEdge: bottomEdge || undefined,
          }),
          raisesAbovePage && 'z-nav',
          className,
        )}
        aria-label='Main navigation'
        {...props}
        data-hen-id={COMPONENT_IDS.Navbar}
      >
        {/* Dim overlay — covers viewport below the top nav while SubNav is open */}
        {dimsPage && !isEditing && hasNavSubmenu(panelItem) && (
          <div
            className={cn('fixed inset-0 bg-nav-overlay', menuFadeClass)}
            onClick={isPanelClosing ? undefined : closeSubNav}
            aria-hidden='true'
          />
        )}

        <div
          ref={containerRef}
          className='relative z-10 bg-white'
          onMouseLeave={
            useHierarchical && !isEditing ? scheduleClose : undefined
          }
          onMouseEnter={useHierarchical ? cancelScheduledClose : undefined}
        >
          <NavbarBarFrame
            capped={isBarCapped}
            rowClassName={navbarBarRowVariants({ contentWidth })}
            paddingClassName={NAVBAR_BAR_PX[barPadding]}
            onMouseOver={
              useHierarchical && !isEditing ? handleNavMouseOver : undefined
            }
          >
            {isLogoCentered && Logo && (
              <div className='pointer-events-none absolute inset-0 flex justify-center items-center'>
                <div className='pointer-events-auto inline-flex'>{Logo}</div>
              </div>
            )}

            <div
              className={cn(
                'flex min-w-0 items-center',
                SPACE_GAP[logoGap || '2xl'],
              )}
            >
              {menuTriggerPlacement === 'leading' && menuDrawer}
              {!isLogoCentered && Logo}

              {/* Desktop links — hierarchical items */}
              {useHierarchical && (
                <nav
                  className={cn(
                    'hidden items-center gap-lg',
                    collapseClasses.desktopFlex,
                    isHighlight && NAV_HIGHLIGHT_GAP_CLASS,
                  )}
                  aria-label='Top-level navigation'
                >
                  {items.map((item, index) => {
                    const hasChildren = hasNavSubmenu(item);
                    const triggerAsLink = hasChildren && hasValidHref(item);
                    const isExpanded = activeSubNav === index;
                    // Stable id linking the trigger to its disclosure panel
                    // (W3C ARIA disclosure pattern: aria-expanded +
                    // aria-controls, no aria-haspopup since the panel is a
                    // region — not a `role="menu"`). The panel is only
                    // mounted while expanded, so `aria-controls` is emitted
                    // only when the target id actually exists in the DOM —
                    // dangling `aria-controls` confuses some assistive tech.
                    const panelId = `subnav-panel-${item.id}`;
                    const ariaControls = isExpanded ? panelId : undefined;
                    // `hover:text-nav-link-hover` overrides the Link default
                    // `hover:text-link-hover` so the link-trigger keeps the nav
                    // color (black unless themed) on hover, matching the
                    // adjacent button-trigger (no-uri) case.
                    // `hover:decoration-neutral-100` keeps the underline
                    // gray; the default variant's hover decoration would
                    // otherwise follow currentColor. Same tailwind-merge
                    // dedupe pattern used in DesktopSidebar.
                    const triggerClass = cn(
                      'inline-flex items-center rounded-xs font-body text-b5 text-nav-link decoration-neutral-100 decoration-2 underline-offset-8 focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-offset-focus focus-visible:ring-offset-1 hover:underline hover:text-nav-link-hover hover:decoration-neutral-100',
                      linkWeightClass,
                      NAV_LINK_TRANSITION_CLASS,
                      isHighlight && NAV_HIGHLIGHT_CLASS,
                      isExpanded &&
                        (isHighlight
                          ? NAV_HIGHLIGHT_ACTIVE_CLASS
                          : 'underline'),
                    );

                    if (triggerAsLink) {
                      return (
                        <Link
                          key={item.id}
                          href={item.uri}
                          underlineStyle='thin'
                          data-nav-index={index}
                          aria-expanded={isExpanded}
                          aria-controls={ariaControls}
                          className={triggerClass}
                          onKeyDown={(
                            e: React.KeyboardEvent<HTMLAnchorElement>,
                          ) => {
                            // ArrowDown opens the SubNav without navigating —
                            // mirrors the standard mega-menu disclosure pattern
                            // so keyboard users can reach the panel without
                            // leaving the page.
                            if (e.key === 'ArrowDown') {
                              e.preventDefault();
                              triggerRef.current = e.currentTarget;
                              openedViaKeyboard.current = true;
                              setActiveSubNav(index);
                            }
                          }}
                        >
                          {item.label}
                        </Link>
                      );
                    }

                    if (hasChildren) {
                      return (
                        <button
                          key={item.id}
                          type='button'
                          data-nav-index={index}
                          aria-expanded={isExpanded}
                          aria-controls={ariaControls}
                          className={triggerClass}
                          onClick={() => {
                            setActiveSubNav((prev) =>
                              prev === index ? null : index,
                            );
                          }}
                          onKeyDown={(
                            e: React.KeyboardEvent<HTMLButtonElement>,
                          ) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              triggerRef.current = e.currentTarget;
                              openedViaKeyboard.current = true;
                            }
                          }}
                        >
                          {item.label}
                        </button>
                      );
                    }

                    // `hover:decoration-neutral-100` drops Link black's
                    // `hover:decoration-link-underline-hover` (same twMerge
                    // pattern as menu triggers above).
                    const leafClass = cn(
                      'inline-flex items-center rounded-xs text-b5 text-nav-link decoration-neutral-100 decoration-2 underline-offset-8 focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-offset-focus focus-visible:ring-offset-1 hover:underline hover:text-nav-leaf-hover hover:decoration-neutral-100',
                      linkWeightClass,
                      NAV_LINK_TRANSITION_CLASS,
                      isHighlight && NAV_HIGHLIGHT_CLASS,
                      activeSubNav === index && 'underline',
                    );
                    if (!hasValidHref(item)) {
                      return (
                        <span key={item.id} className={leafClass}>
                          {item.label}
                        </span>
                      );
                    }

                    return (
                      <Link
                        key={item.id}
                        href={item.uri}
                        data-nav-index={index}
                        variant='black'
                        underline='hover'
                        underlineStyle='thin'
                        className={leafClass}
                      >
                        {item.label}
                      </Link>
                    );
                  })}
                </nav>
              )}

              {/* Desktop links — simple flat links */}
              {!useHierarchical && links.length > 0 && (
                <div
                  className={cn(
                    'hidden items-center gap-2xl',
                    collapseClasses.desktopFlex,
                    isHighlight && NAV_HIGHLIGHT_GAP_CLASS,
                  )}
                >
                  {links.map((link, index) => {
                    // `hover:decoration-current` drops Link black's
                    // underline-hover token so flat nav links keep a
                    // text-colored underline.
                    const className = cn(
                      'inline-flex items-center rounded-xs text-b5 text-nav-link underline-offset-8 hover:text-nav-leaf-hover hover:decoration-current focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-offset-focus focus-visible:ring-offset-1',
                      linkWeightClass,
                      NAV_LINK_TRANSITION_CLASS,
                      isHighlight && NAV_HIGHLIGHT_CLASS,
                    );
                    if (isTextOnlyNavLink(link)) {
                      return (
                        <span
                          key={`${link.href}-${index}`}
                          className={className}
                        >
                          {link.label}
                        </span>
                      );
                    }
                    return (
                      <Link
                        key={`${link.href}-${index}`}
                        href={link.href}
                        variant='black'
                        underline='hover'
                        underlineStyle='thin'
                        aria-current={link.active ? 'page' : undefined}
                        className={className}
                        onClick={link.onClick}
                      >
                        {link.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {actions && (
              <div
                className={cn(
                  'items-center gap-xxs self-stretch',
                  showMobileActions
                    ? 'flex'
                    : cn('hidden', collapseClasses.desktopFlex),
                )}
              >
                <PopoverInsideNavbarBarProvider value={true}>
                  {actions}
                </PopoverInsideNavbarBarProvider>
              </div>
            )}

            {menuTriggerPlacement === 'trailing' && menuDrawer}
          </NavbarBarFrame>

          {/* Desktop SubNav dropdown */}
          {hasNavSubmenu(panelItem) && (
            <div
              ref={subNavRef}
              id={`subnav-panel-${panelItem.id}`}
              role='region'
              aria-label={`${panelItem.label} submenu`}
              data-panel-shape={panelShape}
              data-state={isPanelClosing ? 'closing' : undefined}
              aria-hidden={isPanelClosing || undefined}
              {...(isPanelClosing
                ? { inert: 'true' as unknown as boolean }
                : {})}
              className={cn(
                navbarPanelVariants({
                  panelShape,
                  panelWidth: resolvedPanelWidth,
                  collapseBelow: collapse,
                }),
                panelShape === 'card' &&
                  isPanelCapped &&
                  NAVBAR_BAR_PX_DESKTOP[collapse][barPadding],
                menuFadeClass,
              )}
              onAnimationEnd={(event) => {
                if (isPanelClosing && event.target === event.currentTarget) {
                  finishClosing();
                }
              }}
            >
              <SubNavPanel
                title={panelItem.label}
                sections={panelItem.children}
                layout={panelItem.layout}
                cta={subNavCta}
                dividedLastColumn={dividedLastColumn}
                shadow={false}
                rounded={panelShape !== 'full-width'}
                contentClassName={
                  panelShape === 'full-width' && isPanelCapped
                    ? navbarPanelContentVariants({
                        panelWidth: resolvedPanelWidth,
                      })
                    : undefined
                }
                className={cn(
                  navbarPanelSurfaceVariants({
                    panelShape,
                    panelShadow: resolvedPanelShadow,
                  }),
                  panelShape === 'full-width' &&
                    isPanelCapped &&
                    NAVBAR_BAR_PX[barPadding],
                )}
                menuContent={menuContent}
                showHeading={showMenuHeading}
                menuTransition={menuTransition}
                columnWidth={menuColumns}
                panelStyle={panelStyle}
              />
            </div>
          )}
        </div>
      </nav>
    );
  },
);

Navbar.displayName = 'Navbar';
